<!--
 * @Description: answerPop.vue
-->
<script setup>
import { ref, defineProps, defineModel, watch, nextTick } from "vue"
import { IonButton } from "@ionic/vue"
import store from "@/js/store"

const props = defineProps({
  answerPopData: {
    type: Object,
    default: () => ({}),
  },
  readingId: {
    type: [String, Number],
    default: "",
  },
  studentId: {
    type: [String, Number],
    default: "",
  },
})
const answerPopShow = defineModel("show")
const optionRef = ref(null)
const showAnswerVisible = ref(false)

function getQuestionKey() {
  return String(props.answerPopData?.idx ?? "unknown")
}

function getStudentKey() {
  return String(props.studentId || store.getStudentId?.() || "na")
}

function getStorageKey() {
  return `answerPopChoice:${props.readingId || "na"}:${getStudentKey()}`
}

/** 当前文章答案数组：[{ questionIdx, optionIndex, optionText }] */
function loadChoiceList() {
  try {
    const raw = sessionStorage.getItem(getStorageKey())
    if (!raw) return []
    const list = JSON.parse(raw)
    return Array.isArray(list) ? list : []
  } catch (e) {
    return []
  }
}

function saveChoiceList(list) {
  try {
    sessionStorage.setItem(getStorageKey(), JSON.stringify(list))
  } catch (e) {
    // ignore
  }
}

function loadSelectedIndex() {
  const questionIdx = getQuestionKey()
  const item = loadChoiceList().find((row) => String(row.questionIdx) === questionIdx)
  return item == null ? null : String(item.optionIndex)
}

function saveSelected(optionIndex, optionText) {
  const questionIdx = getQuestionKey()
  const list = loadChoiceList()
  const nextItem = {
    questionIdx,
    optionIndex: Number(optionIndex),
    optionText: (optionText || "").trim(),
  }
  const existIdx = list.findIndex((row) => String(row.questionIdx) === questionIdx)
  if (existIdx >= 0) {
    list[existIdx] = nextItem
  } else {
    list.push(nextItem)
  }
  saveChoiceList(list)
}

function isOptionCell(td) {
  const text = (td.textContent || "").trim()
  if (!text) return false
  // 题号单元格（如 1、）不可选
  if (td.querySelector(".examination-option") && /^\d+\s*、?$/.test(text)) {
    return false
  }
  return true
}

function clearSelectedClass(root) {
  root.querySelectorAll(".answer-option-selected").forEach((el) => {
    el.classList.remove("answer-option-selected")
  })
}

function bindOptionClicks() {
  nextTick(() => {
    const root = optionRef.value
    if (!root) return

    const selected = loadSelectedIndex()
    const tds = root.querySelectorAll("td")

    tds.forEach((td, index) => {
      if (!isOptionCell(td)) return

      const key = String(index)
      td.classList.add("answer-option-cell")
      td.classList.toggle("answer-option-selected", selected === key)

      td.onclick = (e) => {
        e.stopPropagation()
        clearSelectedClass(root)
        td.classList.add("answer-option-selected")
        saveSelected(key, td.textContent)
      }
    })
  })
}

const closeAnswerPop = () => {
  answerPopShow.value = !answerPopShow.value
}

watch(
  () => answerPopShow.value,
  (newVal) => {
    showAnswerVisible.value = false
    if (newVal) {
      bindOptionClicks()
    }
  }
)

watch(
  () => props.answerPopData,
  () => {
    if (answerPopShow.value) {
      showAnswerVisible.value = false
      bindOptionClicks()
    }
  }
)

function showAnswer() {
  showAnswerVisible.value = !showAnswerVisible.value
}
</script>

<template>
  <div class="answerPop" :class="{ slide_out: !answerPopShow, slide_in: answerPopShow }">
    <div class="answerPopHeader">
      <img src="@/assets/answerPop_header.png" alt="" @click="closeAnswerPop" />
    </div>
    <div class="answerPopContent">
      <div
        id="el-examination-option"
        ref="optionRef"
        v-html="props.answerPopData?.option?.outerHTML"
      ></div>
      <div class="answer-content">
        <ion-button color="success" @click="showAnswer">答案</ion-button>
        <span v-if="showAnswerVisible" id="elAnswer" v-html="props?.answerPopData?.answer"></span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.answerPop {
  position: absolute;
  left: 0;
  width: 100%;
  height: 200px;
  z-index: 9999;
}
.answerPopHeader {
  width: 100%;
  height: 20px;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  > img {
    height: 20px;
    width: auto;
  }
}
.answerPopContent {
  width: 100%;
  height: calc(100% - 20px);
  background-color: #fff;
  padding: 20px;
  overflow-y: auto;
}
.slide_in {
  transition: all 0.5s ease-in-out;
  bottom: 0;
}
.slide_out {
  transition: all 0.5s ease-in-out;
  bottom: -300px;
}

:deep(.examination-option-table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0.35rem 0.4rem;
}

:deep(.answer-option-cell) {
  position: relative;
  cursor: pointer;
  padding: 0.45rem 0.55rem;
  border-radius: 0.5rem;
  background: #f5f7f9;
  border: 1px solid transparent;
  color: #2a2f36;
  line-height: 1.45;
  vertical-align: top;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease,
    box-shadow 0.18s ease, transform 0.12s ease;

  &:active {
    transform: scale(0.98);
  }
}

:deep(.answer-option-selected) {
  background: linear-gradient(135deg, #e8f8ef 0%, #d9f3e4 100%);
  border-color: #28ba62;
  color: #167a42;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(40, 186, 98, 0.18);

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.35rem;
    bottom: 0.35rem;
    width: 3px;
    border-radius: 0 2px 2px 0;
    background: #28ba62;
  }
}
</style>
